import { LawTerm } from "@/models/law-term/LawTerm";

export type MatchType = {
  start: number;
  end: number;
  term: string;
  originalTerm: string;
  definition: string;
};

// Sort terms by descending length to match longer terms first
export function getSortedTerms(dictionary: LawTerm[]): LawTerm[] {
  return [...dictionary].sort((a, b) => b.term.length - a.term.length);
}

// Create a map of lowercase term to definition
export function getTermDefinitionMap(
  dictionary: LawTerm[]
): Record<string, string> {
  const map: Record<string, string> = {};
  dictionary.forEach(({ term, definition }) => {
    map[term.toLowerCase()] = definition;
  });
  return map;
}

// Identify ranges in the markdown text that are already links
export function getMarkdownLinkRanges(markdownText: string) {
  const linkRanges: Array<{ start: number; end: number }> = [];
  const linkRegex = /\[[^\]]+?\]\([^)]+\)|\[[^\]]+?\]\[tooltip_[^\]]+\]/g;
  let match;
  while ((match = linkRegex.exec(markdownText)) !== null) {
    linkRanges.push({ start: match.index, end: linkRegex.lastIndex });
  }
  return linkRanges;
}

// Find valid matches of terms in the markdown text that aren't inside links or overlapping
export function getMatchingTerms(
  sortedDict: LawTerm[],
  markdownText: string,
  linkRanges: Array<{ start: number; end: number }>
): Array<{ start: number; end: number; term: string }> {
  const matches: Array<{ start: number; end: number; term: string }> = [];
  let match;

  for (const { term } of sortedDict) {
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const spacedTerm = escapedTerm.replace(/\s+/g, "\\s+");
    const regex = new RegExp(`\\b${spacedTerm}\\b`, "gi");

    while ((match = regex.exec(markdownText)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      const insideLink = linkRanges.some(
        (r) => start >= r.start && end <= r.end
      );
      const overlap = matches.some((m) => !(end <= m.start || start >= m.end));
      if (!insideLink && !overlap) {
        matches.push({ start, end, term: match[0] });
      }
    }
  }
  return matches.sort((a, b) => a.start - b.start);
}

// Inject tooltip markdown syntax into the original text for matched terms
export function insertTooltipsIntoMarkdown(
  markdownText: string,
  matches: Array<{ start: number; end: number; term: string }>,
  termMap: Record<string, string>
): {
  updatedText: string;
  tooltipReferences: string[];
} {
  let resultText = markdownText;
  let offset = 0;
  const uniqueTerms = new Set<string>();
  const tooltipReferences: string[] = [];

  matches.forEach(({ start, end, term }) => {
    const lower = term.toLowerCase();
    const label = lower.replace(/\s+/g, "-");
    const replacement = `[${term}][tooltip_${label}]`;

    resultText =
      resultText.slice(0, start + offset) +
      replacement +
      resultText.slice(end + offset);

    offset += replacement.length - term.length;

    if (!uniqueTerms.has(lower)) {
      uniqueTerms.add(lower);
      tooltipReferences.push(
        `[tooltip_${label}]: ## "${termMap[lower]}|${term}"`
      );
    }
  });

  return { updatedText: resultText, tooltipReferences };
}

// Wrap terms in markdown text with tooltip markdown references
export function wrapTermsWithTooltip(
  markdownText: string,
  dictionary: LawTerm[]
): string {
  const sortedDict = getSortedTerms(dictionary);
  const termMap = getTermDefinitionMap(sortedDict);
  const linkRanges = getMarkdownLinkRanges(markdownText);
  const matches = getMatchingTerms(sortedDict, markdownText, linkRanges);

  const { updatedText, tooltipReferences } = insertTooltipsIntoMarkdown(
    markdownText,
    matches,
    termMap
  );

  return `${tooltipReferences.join("\n")}` + "\n\n" + updatedText;
}

// Check if range inside link or overlaps with existing matches
export function isInLinkOrOverlap(
  start: number,
  end: number,
  linkRanges: { start: number; end: number }[],
  matches: MatchType[]
) {
  return (
    linkRanges.some((r) => start >= r.start && end <= r.end) ||
    matches.some((m) => !(end <= m.start || start >= m.end))
  );
}
